import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Input, Space, Tag, Tooltip } from "antd";
import {
  CopyOutlined,
  DeleteOutlined,
  DownOutlined,
  PlusOutlined,
  RightOutlined,
  SafetyCertificateOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { Button as AntButton } from "antd";
import {
  Badge,
  Button,
  PageHeader,
  PageSection,
  StatePanel,
  formatDate,
  toneForStatus,
} from "@anyseal/ui";
import { portalClient } from "../api";
import { ConfirmModal } from "../components/ConfirmModal";
import { FormModal } from "../components/FormModal";
import { useI18n } from "../i18n";
import { statusLabel } from "../status";
import { projectPath, useWorkspace } from "../workspace";
import { WorkspaceGuard } from "../WorkspaceGuard";

interface UsagePolicy {
  allowed_models: string[];
  allowed_cidrs: string[];
  rate_limit_requests: number;
  rate_limit_window_seconds: number;
  quota_microunits?: number | null;
  expires_at?: string | null;
  routing_group?: string;
  used_microunits: number;
  reserved_microunits: number;
}

interface Credential {
  id: string;
  name: string;
  key_prefix: string;
  status: string;
  policy: UsagePolicy;
  version: number;
  last_used_at?: string;
  created_at: string;
}

interface Issued {
  credential: Credential;
  token: string;
  token_notice: string;
}

type ConfirmMode = "revoke" | "rotate";

function SecretBox({ token, locale, onDismiss }: { token: string; locale: "zh-CN" | "en"; onDismiss: () => void }) {
  const copy = () => void navigator.clipboard.writeText(token);
  return (
    <div className="portal-credential-secret">
      <div className="portal-key-secret">
        <span>{token}</span>
        <button
          className="portal-key-secret__copy"
          onClick={copy}
          aria-label={locale === "zh-CN" ? "复制密钥" : "Copy key"}
        >
          <CopyOutlined />
        </button>
      </div>
      <p className="portal-key-secret__warning">
        {locale === "zh-CN"
          ? "请立即保存此密钥。关闭后将无法再次查看完整密钥。"
          : "Copy this key now. You won't be able to see it again."}
      </p>
      <div>
        <Button variant="secondary" onClick={onDismiss}>
          {locale === "zh-CN" ? "我已安全保存" : "I stored it securely"}
        </Button>
      </div>
    </div>
  );
}

function CredentialCard({
  credential,
  issued,
  canManage,
  locale,
  onRotate,
  onRevoke,
  onDismissSecret,
}: {
  credential: Credential;
  issued: Issued | null;
  canManage: boolean;
  locale: "zh-CN" | "en";
  onRotate: (c: Credential) => void;
  onRevoke: (c: Credential) => void;
  onDismissSecret: () => void;
}) {
  const isChinese = locale === "zh-CN";
  const isJustIssued = issued?.credential.id === credential.id;
  const copyPrefix = () => void navigator.clipboard.writeText(credential.key_prefix);
  const isRevoked = credential.status === "revoked";
  const scopeList = credential.policy.allowed_models?.length
    ? credential.policy.allowed_models
    : [];
  const quota = credential.policy.quota_microunits;
  const rate = credential.policy.rate_limit_requests;

  return (
    <PageSection
      title={credential.name}
      description={`${isChinese ? "创建于" : "Created"} ${formatDate(credential.created_at, locale)}${credential.last_used_at ? ` · ${isChinese ? "最后使用" : "Last used"} ${formatDate(credential.last_used_at, locale)}` : ""}`}
      action={<Badge tone={toneForStatus(credential.status)}>{statusLabel(credential.status, locale)}</Badge>}
    >
      {/* Key preview row */}
      <div className="portal-key-preview">
        <code>{credential.key_prefix}</code>
        <span className="portal-key-preview__mask">••••••••</span>
        <button
          className="portal-key-preview__copy"
          onClick={copyPrefix}
          aria-label={isChinese ? "复制密钥前缀" : "Copy key prefix"}
        >
          <CopyOutlined />
        </button>
      </div>

      {/* Scopes row */}
      {scopeList.length > 0 ? (
        <div className="portal-credential-tags">
          {scopeList.map((model) => (
            <Tag key={model} style={{ margin: 0 }}>{model}</Tag>
          ))}
        </div>
      ) : (
        <span className="portal-credential-meta">
          {isChinese ? "全部模型可用" : "All models allowed"}
        </span>
      )}

      {/* Restriction summary */}
      <span className="portal-credential-meta">
        {isChinese ? "限速" : "Rate limit"}: {rate}/{credential.policy.rate_limit_window_seconds ?? 60}s
        {quota ? ` · ${isChinese ? "额度" : "Quota"}: ${quota / 1_000_000}` : ""}
        {credential.policy.expires_at ? ` · ${isChinese ? "到期" : "Expires"}: ${formatDate(credential.policy.expires_at, locale)}` : ""}
      </span>

      {/* Just-created secret */}
      {isJustIssued && issued ? (
        <SecretBox
          token={issued.token}
          locale={locale}
          onDismiss={onDismissSecret}
        />
      ) : null}

      {/* Card actions live at the bottom, not in the heading. */}
      <div className="portal-credential-actions">
        <Tooltip title={isChinese ? "轮换密钥" : "Rotate key"}>
          <AntButton
            size="small"
            icon={<SyncOutlined />}
            disabled={!canManage || isRevoked}
            onClick={() => onRotate(credential)}
          >
            {isChinese ? "轮换" : "Rotate"}
          </AntButton>
        </Tooltip>
        <Tooltip title={isChinese ? "撤销密钥" : "Revoke key"}>
          <AntButton
            size="small"
            danger
            icon={<DeleteOutlined />}
            disabled={!canManage || isRevoked}
            onClick={() => onRevoke(credential)}
          >
            {isChinese ? "撤销" : "Revoke"}
          </AntButton>
        </Tooltip>
      </div>
    </PageSection>
  );
}

export function CredentialsPageInner() {
  const { locale } = useI18n();
  const { organization, project } = useWorkspace();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [models, setModels] = useState("");
  const [cidrs, setCidrs] = useState("");
  const [rateLimit, setRateLimit] = useState("60");
  const [quota, setQuota] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [issued, setIssued] = useState<Issued | null>(null);
  const [confirmMode, setConfirmMode] = useState<ConfirmMode | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<Credential | null>(null);
  const [confirmation, setConfirmation] = useState("");
  const base = organization && project ? `${projectPath(organization.id, project.id)}/credentials` : "";
  const query = useQuery({ queryKey: ["portal", "credentials", base], queryFn: () => portalClient.get<Credential[]>(base), enabled: Boolean(base) });
  const canManage = organization ? ["owner", "admin", "developer"].includes(organization.role) : false;

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ["portal", "credentials", base] });
  const closeConfirmation = () => { setConfirmMode(null); setConfirmTarget(null); setConfirmation(""); };
  const create = useMutation({
    mutationFn: () => portalClient.post<Issued>(base, {
      name: name.trim(),
      policy: {
        allowed_models: lines(models),
        allowed_cidrs: lines(cidrs),
        rate_limit_requests: Math.max(1, Math.round(Number(rateLimit) || 60)),
        rate_limit_window_seconds: 60,
        quota_microunits: quota.trim() ? Math.round(Number(quota) * 1_000_000) : null,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
        routing_group: "",
        used_microunits: 0,
        reserved_microunits: 0,
      },
    }),
    onSuccess: (value) => {
      setIssued(value);
      setShowCreate(false);
      setName(""); setModels(""); setCidrs(""); setRateLimit("60"); setQuota(""); setExpiresAt("");
      setShowAdvanced(false);
      refresh();
    },
  });
  const revoke = useMutation({
    mutationFn: (credential: Credential) => portalClient.delete<{ status: string }>(`${base}/${credential.id}`, { expected_version: credential.version, reason: "revoked by project member from Portal" }),
    onSuccess: () => { closeConfirmation(); refresh(); },
  });
  const rotate = useMutation({
    mutationFn: (credential: Credential) => portalClient.post<Issued>(`${base}/${credential.id}/rotate`, { expected_version: credential.version, reason: "rotated by project member from Portal" }),
    onSuccess: (value) => { setIssued(value); closeConfirmation(); refresh(); },
  });

  function openConfirmation(mode: ConfirmMode, target: Credential) {
    setConfirmMode(mode);
    setConfirmTarget(target);
    setConfirmation("");
  }

  function submitConfirmation() {
    if (!confirmTarget || confirmation !== confirmTarget.name) return;
    if (confirmMode === "revoke") revoke.mutate(confirmTarget);
    if (confirmMode === "rotate") rotate.mutate(confirmTarget);
  }

  // How many advanced restrictions differ from the defaults — drives the
  // collapsed summary so the user can see the policy without expanding.
  const advancedCount = [
    models.trim(),
    cidrs.trim(),
    quota.trim(),
    expiresAt,
    Number(rateLimit) > 0 && rateLimit !== "60" ? "1" : "",
  ].filter(Boolean).length;

  if (query.isLoading) {
    return (
      <div className="portal-page-grid">
        <StatePanel kind="loading" title={locale === "zh-CN" ? "正在加载 API 密钥" : "Loading API keys"} />
      </div>
    );
  }

  if (query.error) {
    return (
      <div className="portal-page-grid">
        <StatePanel
          kind="error"
          title={locale === "zh-CN" ? "无法加载 API 密钥" : "API keys unavailable"}
          description={query.error.message}
          action={
            <Button variant="secondary" onClick={() => void query.refetch()}>
              {locale === "zh-CN" ? "重新加载" : "Retry"}
            </Button>
          }
        />
      </div>
    );
  }

  const credentials = query.data ?? [];
  const isConfirming = revoke.isPending || rotate.isPending;

  return (
    <div className="portal-page-grid">
      <PageHeader
        eyebrow={locale === "zh-CN" ? "工作区" : "Workspace"}
        title={locale === "zh-CN" ? "API 密钥" : "API keys"}
        description={
          locale === "zh-CN"
            ? "为每个应用创建独立密钥，并限制模型、网络、速率、额度和有效期。密钥只会完整显示一次。"
            : "Create one key per application and restrict models, networks, rate, quota, and expiry. Secrets are shown once."
        }
        actions={
          <Button disabled={!canManage} onClick={() => setShowCreate(true)}>
            <Space size={6}>
              <PlusOutlined />
              {locale === "zh-CN" ? "创建密钥" : "Create API key"}
            </Space>
          </Button>
        }
      />

      {!canManage ? (
        <Alert
          className="portal-credentials-page__notice"
          type="info"
          showIcon
          icon={<SafetyCertificateOutlined />}
          message={
            locale === "zh-CN"
              ? "当前角色可以查看密钥，但不能创建、轮换或撤销密钥。"
              : "Your current role can view keys but cannot create, rotate, or revoke them."
          }
        />
      ) : null}

      {credentials.length > 0 ? (
        <div className="portal-cred-stats">
          <div className="portal-cred-stat">
            <div className="portal-cred-stat__value">{credentials.length}</div>
            <div className="portal-cred-stat__label">{locale === "zh-CN" ? "全部密钥" : "Total keys"}</div>
          </div>
          <div className="portal-cred-stat">
            <div className="portal-cred-stat__value">
              {credentials.filter((c) => c.status === "active" || c.status === "enabled").length}
            </div>
            <div className="portal-cred-stat__label">{locale === "zh-CN" ? "启用中" : "Active keys"}</div>
          </div>
          <div className="portal-cred-stat">
            <div className="portal-cred-stat__value">
              {credentials.filter((c) => c.status === "revoked").length}
            </div>
            <div className="portal-cred-stat__label">{locale === "zh-CN" ? "已撤销" : "Revoked keys"}</div>
          </div>
        </div>
      ) : null}

      {credentials.length === 0 ? (
        <StatePanel
          kind="empty"
          title={locale === "zh-CN" ? "还没有 API 密钥" : "No API keys yet"}
          description={
            locale === "zh-CN"
              ? "创建第一把密钥，开始调用 API。"
              : "Create your first key to start making API calls."
          }
          action={
            canManage ? (
              <Button onClick={() => setShowCreate(true)}>
                <Space size={6}>
                  <PlusOutlined />
                  {locale === "zh-CN" ? "创建密钥" : "Create API key"}
                </Space>
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="portal-cred-grid">
          {credentials.map((credential) => (
            <CredentialCard
              key={credential.id}
              credential={credential}
              issued={issued}
              canManage={canManage}
              locale={locale}
              onRotate={(c) => openConfirmation("rotate", c)}
              onRevoke={(c) => openConfirmation("revoke", c)}
              onDismissSecret={() => setIssued(null)}
            />
          ))}
        </div>
      )}

      {/* Create modal */}
      <FormModal
        open={showCreate}
        title={locale === "zh-CN" ? "创建 API 密钥" : "Create API key"}
        description={
          locale === "zh-CN"
            ? "密钥只在创建后显示一次，请立即保存。"
            : "The key is shown only once after creation. Save it immediately."
        }
        onCancel={() => setShowCreate(false)}
        onSubmit={() => create.mutate()}
        submitLabel={locale === "zh-CN" ? "创建" : "Create"}
        busy={create.isPending}
        disabled={!name.trim() || (quota.trim() !== "" && !(Number(quota) > 0))}
        error={create.error}
      >
        <label>
          <span>{locale === "zh-CN" ? "名称" : "Name"}</span>
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={locale === "zh-CN" ? "例如 production-api" : "For example, production-api"}
            maxLength={80}
            autoFocus
          />
        </label>

        <div className="portal-key-advanced">
          <button
            type="button"
            className="portal-key-advanced__toggle"
            aria-expanded={showAdvanced}
            onClick={() => setShowAdvanced((current) => !current)}
          >
            {showAdvanced ? <DownOutlined /> : <RightOutlined />}
            <span>{locale === "zh-CN" ? "高级设置" : "Advanced settings"}</span>
            {advancedCount > 0 ? (
              <span className="portal-key-advanced__count">
                {locale === "zh-CN" ? `已配置 ${advancedCount} 项` : `${advancedCount} set`}
              </span>
            ) : null}
          </button>
          {showAdvanced ? (
            <div className="portal-key-advanced__body">
              <label>
                <span>
                  {locale === "zh-CN" ? "允许的模型" : "Allowed models"}
                  <em>{locale === "zh-CN" ? "每行一个，留空表示全部模型" : "One per line. Leave empty to allow all models."}</em>
                </span>
                <Input.TextArea
                  value={models}
                  onChange={(event) => setModels(event.target.value)}
                  autoSize={{ minRows: 2, maxRows: 6 }}
                />
              </label>
              <label>
                <span>
                  {locale === "zh-CN" ? "允许的 CIDR" : "Allowed CIDRs"}
                  <em>{locale === "zh-CN" ? "每行一个，留空表示不限制来源" : "One per line. Leave empty to allow any source."}</em>
                </span>
                <Input.TextArea
                  value={cidrs}
                  onChange={(event) => setCidrs(event.target.value)}
                  autoSize={{ minRows: 2, maxRows: 6 }}
                />
              </label>
              <div className="portal-key-advanced__grid">
                <label>
                  <span>
                    {locale === "zh-CN" ? "速率限制（次 / 分钟）" : "Rate limit (req / min)"}
                    <em>{locale === "zh-CN" ? "超出后拒绝至下一分钟窗口。" : "Rejected until the next one-minute window."}</em>
                  </span>
                  <Input
                    type="number" min={1} step={1}
                    value={rateLimit}
                    onChange={(event) => setRateLimit(event.target.value)}
                  />
                </label>
                <label>
                  <span>
                    {locale === "zh-CN" ? "额度上限（可选）" : "Quota cap (optional)"}
                    <em>{locale === "zh-CN" ? "留空表示不设上限。" : "Leave empty for no cap."}</em>
                  </span>
                  <Input
                    type="number" min={0} step="0.01"
                    value={quota}
                    onChange={(event) => setQuota(event.target.value)}
                  />
                </label>
                <label>
                  <span>
                    {locale === "zh-CN" ? "有效期（可选）" : "Expiry (optional)"}
                    <em>{locale === "zh-CN" ? "到期后密钥自动失效。" : "The key stops working after this date."}</em>
                  </span>
                  <Input
                    type="date"
                    value={expiresAt}
                    onChange={(event) => setExpiresAt(event.target.value)}
                  />
                </label>
              </div>
            </div>
          ) : (
            <p className="portal-key-advanced__summary">
              {advancedCount > 0
                ? locale === "zh-CN"
                  ? `已配置 ${advancedCount} 项限制，展开可调整。`
                  : `${advancedCount} restriction${advancedCount > 1 ? "s" : ""} configured — expand to adjust.`
                : locale === "zh-CN"
                  ? "默认策略：允许全部模型、不限来源、60 次 / 分钟、无额度与有效期限制。"
                  : "Defaults: all models, any source, 60 requests / minute, no quota cap, no expiry."}
            </p>
          )}
        </div>
      </FormModal>

      {/* Confirm rotate / revoke modal */}
      <ConfirmModal
        open={Boolean(confirmMode && confirmTarget)}
        title={
          confirmMode === "revoke"
            ? locale === "zh-CN" ? "撤销 API 密钥" : "Revoke API key"
            : locale === "zh-CN" ? "轮换 API 密钥" : "Rotate API key"
        }
        description={
          confirmMode === "revoke"
            ? locale === "zh-CN"
            ? "撤销后该密钥立即失效，使用它的服务将无法访问 API。此操作不可撤销。"
            : "Revoking takes effect immediately. Services using this key will lose API access. This cannot be undone."
            : locale === "zh-CN"
            ? "轮换会生成新密钥并立即失效旧密钥。请在轮换后更新你的服务配置。"
            : "Rotating issues a new key and immediately invalidates the old one. Update your services after rotating."
        }
        confirmLabel={
          confirmMode === "revoke"
            ? locale === "zh-CN" ? "撤销密钥" : "Revoke key"
            : locale === "zh-CN" ? "轮换密钥" : "Rotate key"
        }
        expectedText={confirmTarget?.name}
        confirmationText={confirmation}
        onConfirmationText={setConfirmation}
        onConfirm={submitConfirmation}
        onCancel={closeConfirmation}
        busy={isConfirming}
        danger={confirmMode === "revoke"}
        error={revoke.error ?? rotate.error}
      />
    </div>
  );
}

const lines = (value: string): string[] => value.split(/\r?\n|,/).map((item) => item.trim()).filter(Boolean);

export function CredentialsPage() {
  return <WorkspaceGuard><CredentialsPageInner /></WorkspaceGuard>;
}
